import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Save, Image as ImageIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Toaster, toast } from "sonner";
import MediaPicker from "@/admin/components/MediaPicker";
import RichTextEditor from "@/admin/components/RichTextEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auto-derive initials from a full name — same convention used on the
// public Why Antera page when no photo is uploaded.
function deriveInitials(fullName) {
  const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const EMPTY = {
  name: "",
  title: "",
  bio: "",
  initials: "",
  photo_url: "",
  order: 0,
  published: true,
};

export default function AdminTeamEditPage() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const isNew = !memberId || memberId === "new";

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/team/${memberId}`);
        setForm({ ...EMPTY, ...data });
      } catch {
        toast.error("Could not load team member.");
      } finally {
        setLoading(false);
      }
    })();
  }, [memberId, isNew]);

  const setField = (key) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.title.trim()) {
      toast.error("Name and Title are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Auto-derive initials if the admin left the field blank so the
        // fallback badge on the public page always renders something sensible.
        initials: form.initials?.trim() || deriveInitials(form.name),
      };
      if (isNew) {
        const { data } = await axios.post(`${API}/admin/team`, payload);
        toast.success("Team member created.");
        navigate(`/admin/team/${data.id}`);
      } else {
        await axios.patch(`${API}/admin/team/${memberId}`, payload);
        toast.success("Saved.");
      }
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-sm text-slate-400" data-testid="admin-team-edit-loading">
        Loading…
      </div>
    );
  }

  return (
    <div data-testid="admin-team-edit-page" className="bg-white min-h-full">
      <Toaster position="top-right" richColors />

      <div className="px-5 lg:px-8 py-5 border-b border-slate-200 flex items-center justify-between gap-3">
        <Link
          to="/admin/team"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All team members
        </Link>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Label htmlFor="published-switch" className="text-xs text-slate-500">
              {form.published ? "Published" : "Hidden"}
            </Label>
            <Switch
              id="published-switch"
              checked={form.published}
              onCheckedChange={(v) => setField("published")(v)}
              data-testid="admin-team-published-switch"
            />
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="admin-team-save"
            className="bg-sky-600 hover:bg-sky-700 text-white h-9 rounded-lg text-xs font-semibold"
          >
            <Save className="h-3.5 w-3.5 mr-1" />
            {saving ? "Saving…" : isNew ? "Create" : "Save"}
          </Button>
        </div>
      </div>

      <div className="p-5 lg:p-8 max-w-3xl">
        <div className="space-y-5">
          <div>
            <Label htmlFor="name" className="text-xs font-semibold text-slate-700">
              Full name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={form.name}
              onChange={setField("name")}
              placeholder="e.g. Alfred Mondoux"
              className="mt-1"
              data-testid="admin-team-name"
            />
          </div>

          <div>
            <Label htmlFor="title" className="text-xs font-semibold text-slate-700">
              Job title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              value={form.title}
              onChange={setField("title")}
              placeholder="e.g. Chief Financial Officer"
              className="mt-1"
              data-testid="admin-team-title"
            />
          </div>

          <div>
            <Label htmlFor="initials" className="text-xs font-semibold text-slate-700">
              Initials (fallback badge when no photo)
            </Label>
            <Input
              id="initials"
              value={form.initials}
              onChange={setField("initials")}
              placeholder={deriveInitials(form.name) || "e.g. AM"}
              maxLength={3}
              className="mt-1 w-32"
              data-testid="admin-team-initials"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Leave blank to auto-derive from the name.
            </p>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700">Headshot photo</Label>
            <div className="mt-2 flex items-start gap-4">
              {form.photo_url ? (
                <img
                  src={form.photo_url}
                  alt=""
                  className="h-24 w-24 rounded-full object-cover border border-slate-200"
                  data-testid="admin-team-photo-preview"
                />
              ) : (
                <div className="h-24 w-24 rounded-full bg-sky-100 text-sky-700 text-xl font-bold flex items-center justify-center border border-sky-200">
                  {form.initials || deriveInitials(form.name) || "?"}
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPickerOpen(true)}
                  data-testid="admin-team-photo-pick"
                  className="h-8 text-xs"
                >
                  <ImageIcon className="h-3.5 w-3.5 mr-1" />
                  {form.photo_url ? "Replace photo" : "Choose or upload photo"}
                </Button>
                {form.photo_url && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setField("photo_url")("")}
                    data-testid="admin-team-photo-remove"
                    className="h-8 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 justify-start"
                  >
                    <X className="h-3.5 w-3.5 mr-1" />
                    Remove photo
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="bio" className="text-xs font-semibold text-slate-700">
              Bio
            </Label>
            <div className="mt-1 border border-slate-200 rounded-lg overflow-hidden">
              <RichTextEditor
                value={form.bio || ""}
                onChange={(html) => setField("bio")(html)}
                placeholder="One or two paragraphs about this person's experience and role."
                testId="admin-team-bio-editor"
              />
            </div>
          </div>
        </div>
      </div>

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(url) => {
          setField("photo_url")(url);
          setPickerOpen(false);
        }}
      />
    </div>
  );
}
